/**
 * useDocumentLinks - Hook for managing document links (doc-to-doc, doc-to-issue, doc-to-epic)
 *
 * Features:
 * - Fetch links for a document (all target types)
 * - Create new links (to docs, issues, or epics)
 * - Delete links
 * - Group links by target_type for UI consumption
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  documentsApi,
  type DocumentLinkWithTarget,
  type DocumentRelationType,
  type LinkTargetType,
} from '../api/documents';

interface UseDocumentLinksOptions {
  documentId: string;
  projectId: string;
  enabled?: boolean;
}

export function useDocumentLinks({
  documentId,
  projectId,
  enabled = true,
}: UseDocumentLinksOptions) {
  const queryClient = useQueryClient();

  // Fetch links
  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['document-links', projectId, documentId],
    queryFn: () => documentsApi.links.list(documentId, projectId),
    enabled: enabled && !!documentId && !!projectId,
  });

  // IMPORTANT: derive links/count via useMemo — a bare `data?.links ?? []`
  // produces a fresh `[]` reference on every render whenever data is undefined
  // (initial fetch, refetch, error), which then drives React #185 in downstream
  // useEffect deps. See DocumentationReader's tab-registration effect.
  const links = useMemo(() => data?.links ?? [], [data]);
  const count = data?.count ?? 0;

  // Group links by target type
  const groupedLinks = useMemo(() => {
    const docLinks = links.filter((l) => l.target_type === 'document');
    const issueLinks = links.filter((l) => l.target_type === 'issue');
    const epicLinks = links.filter((l) => l.target_type === 'epic');
    return { docLinks, issueLinks, epicLinks };
  }, [links]);

  // Create doc-to-doc link mutation
  const createMutation = useMutation({
    mutationFn: (data: { targetDocumentId: string; relationType: DocumentRelationType }) =>
      documentsApi.links.create(documentId, projectId, {
        target_document_id: data.targetDocumentId,
        relation_type: data.relationType,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-links', projectId, documentId] });
    },
  });

  // Create entity link mutation (issue/epic)
  const createEntityLinkMutation = useMutation({
    mutationFn: (data: { targetType: LinkTargetType; targetEntityId: string; relationType?: DocumentRelationType }) =>
      documentsApi.links.create(documentId, projectId, {
        target_type: data.targetType,
        target_entity_id: data.targetEntityId,
        relation_type: data.relationType || 'linked',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-links', projectId, documentId] });
    },
  });

  // Delete link mutation
  const deleteMutation = useMutation({
    mutationFn: (relationId: string) =>
      documentsApi.links.delete(documentId, projectId, relationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document-links', projectId, documentId] });
    },
  });

  const createLink = useCallback(
    (targetDocumentId: string, relationType: DocumentRelationType) =>
      createMutation.mutateAsync({ targetDocumentId, relationType }),
    [createMutation]
  );

  const createEntityLink = useCallback(
    (targetType: LinkTargetType, targetEntityId: string, relationType?: DocumentRelationType) =>
      createEntityLinkMutation.mutateAsync({ targetType, targetEntityId, relationType }),
    [createEntityLinkMutation]
  );

  const deleteLink = useCallback(
    (relationId: string) => deleteMutation.mutateAsync(relationId),
    [deleteMutation]
  );

  return {
    links,
    count,
    isLoading,
    error,
    // Grouped for easy UI consumption
    docLinks: groupedLinks.docLinks,
    issueLinks: groupedLinks.issueLinks,
    epicLinks: groupedLinks.epicLinks,
    // Mutations
    createLink,
    createEntityLink,
    deleteLink,
    isCreating: createMutation.isPending || createEntityLinkMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}

export default useDocumentLinks;
